import { useMemo } from "react";
import { createFileRoute, Link, notFound, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeftIcon, CircleNotchIcon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";
import { PlacesNotFoundError } from "common/apis/places";
import { placesCityQueryOptions } from "common/hooks/places/placesQueryOptions";
import type { PlacesCityParams } from "common/types/places";
import { isComplexVenue } from "common/utils/general";
import VenueCard from "@/components/VenueCard/VenueCard";
import { Footer } from "@/components/Footer";
import { NumberedPagination } from "@/components/Pagination/NumberedPagination";
import { PlacesFilterBar } from "@/components/Places/PlacesFilterBar";
import { useSaveVenueFlow } from "@/components/Places/useSaveVenueFlow";
import { PlacesUnavailable } from "@/components/Places/PlacesUnavailable";
import {
  buildPlacesCityHead,
  parsePlacesSearch,
  placesSearch,
  placesVenueToVenue,
} from "@/lib/placesPage";
import { venuePageHref } from "@/lib/whatsNearbyHead";
import { cn } from "@/lib/utils";

const isProduction =
  import.meta.env.VITE_APPLICATION_ENVIRONMENT === "Production";

export const Route = createFileRoute("/places/$citySlug")({
  ssr: true,
  component: PlacesCity,
  errorComponent: PlacesUnavailable,
  validateSearch: (search: Record<string, unknown>): PlacesCityParams => parsePlacesSearch(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, params, deps: { search } }) => {
    const canonicalOrigin =
      (typeof process !== "undefined" && process.env?.PUBLIC_ORIGIN) || "";
    try {
      const response = await context.queryClient.ensureQueryData(
        placesCityQueryOptions(params.citySlug, search),
      );
      return { city: response.data, search, canonicalOrigin };
    } catch (err) {
      if (err instanceof PlacesNotFoundError) throw notFound();
      // PlacesUnavailableError propagates: a search outage is a 5xx, never a 404 or an empty page.
      throw err;
    }
  },
  head: ({ loaderData, params }) =>
    buildPlacesCityHead(
      loaderData?.city,
      params.citySlug,
      loaderData?.search ?? {},
      loaderData?.canonicalOrigin ?? "",
      isProduction,
    ),
});

function PlacesCity() {
  const i18n = useI18N();
  const navigate = useNavigate();
  const { citySlug } = Route.useParams();
  const search = Route.useSearch();
  const { data } = useQuery(placesCityQueryOptions(citySlug, search));
  const saveFlow = useSaveVenueFlow();
  // A page or filter change keeps this list on screen while the loader fetches the next one.
  const isNavigating = useRouterState({ select: (state) => state.isLoading });

  const city = data?.data;
  const venues = useMemo(() => (city?.venues ?? []).map(placesVenueToVenue), [city]);

  if (!city) {
    return null;
  }

  return (
    <>
      <main id="main" className="w-full bg-bg-brand-secondary-default">
        <div className="mx-auto flex max-w-[1160px] flex-col gap-6 px-4 pt-2 pb-6 md:pt-4 md:pb-10">
          <div className="flex flex-col gap-1">
            <Link
              to="/places"
              activeOptions={{ exact: true }}
              className="inline-flex min-h-[44px] items-center gap-2 self-start rounded label-m-semibold text-fg-brand-subtle hover:text-fg-brand-strong focus-blue"
            >
              <ArrowLeftIcon size={20} aria-hidden="true" focusable="false" />
              {i18n.get("placesBackToAllCities")}
            </Link>
            <header className="flex flex-col gap-1">
              <h1 className="headline-l-bold text-fg-neutral-strong">
                {i18n.t("placesCityHeading", { city: city.city.name })}
              </h1>
              <div className="flex items-center gap-2">
                <h2 className="label-m-medium text-fg-neutral-subtle">
                  {i18n.t("placesCityCount", { count: city.total_count })}
                </h2>
                {isNavigating && (
                  <CircleNotchIcon size={18} aria-hidden="true" focusable="false" className="animate-spin text-fg-brand-subtle" />
                )}
              </div>
            </header>
          </div>
          <p role="status" className="sr-only">
            {isNavigating ? i18n.get("loading") : ""}
          </p>

          <PlacesFilterBar
            citySlug={citySlug}
            search={search}
            sections={city.filter_accessibility_results}
          />

          <div
            aria-busy={isNavigating || undefined}
            className={cn("flex flex-col gap-6 transition-opacity", isNavigating && "pointer-events-none opacity-50")}
          >
            {venues.length > 0 ? (
              <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {venues.map((venue) => {
                  const href = venuePageHref(venue);
                  return (
                    <li key={venue.id}>
                      <VenueCard
                        location={venue}
                        href={href}
                        onOpen={() => {
                          if (!href) return;
                          if (isComplexVenue(venue)) {
                            window.open(href, "_blank");
                            return;
                          }
                          // Only in-app opens carry the source, so the crawlable href stays the bare venue URL.
                          void navigate({ to: href, search: { source: "places" } as never });
                        }}
                        savedPlaces={saveFlow.savedPlaces}
                        onSaveVenue={saveFlow.onSaveVenue}
                        isLoggedIn={saveFlow.isLoggedIn}
                        onShowAuthModal={saveFlow.onShowAuthModal}
                        showAddress
                        showVariantIndicators
                        showDescription
                      />
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p role="status" className="label-m-medium text-fg-neutral-subtle">
                {i18n.get("placesNoVenues")}
              </p>
            )}

            <NumberedPagination
              currentPage={city.current_page}
              totalPages={city.total_pages}
              renderPageLink={(page, children, props) => (
                <Link
                  to={`/places/${citySlug}`}
                  search={placesSearch({ ...search, page: page > 1 ? page : undefined })}
                  activeOptions={{ exact: true }}
                  {...props}
                >
                  {children}
                </Link>
              )}
            />
          </div>

          {city.curated_lists.length > 0 && (
            <section aria-labelledby="places-curated-lists" className="flex flex-col gap-3">
              <h2 id="places-curated-lists" className="title-m-bold text-fg-neutral-strong">
                {i18n.t("placesCuratedListsHeading", { city: city.city.name })}
              </h2>
              <ul className="flex flex-wrap gap-2">
                {city.curated_lists.map((list) => (
                  <li key={list.slug}>
                    <Link
                      to={`/whats-nearby/${list.slug}`}
                      onClick={(e) => {
                        // Only in-app opens carry the source, so the crawlable href stays the bare list URL.
                        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        e.preventDefault();
                        void navigate({ to: `/whats-nearby/${list.slug}`, search: { source: "places" } as never });
                      }}
                      className="inline-flex min-h-[44px] items-center rounded-lg border border-border-token-brand-minimal bg-bg-brand-secondary-default px-3 label-m-medium text-fg-neutral-strong hover:bg-bg-brand-secondary-hover focus-blue"
                    >
                      {list.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </main>
      <Footer />
      {saveFlow.saveModal}
    </>
  );
}
